import React, { useMemo, useState } from "react"
import {
  faPhone,
  faLocationDot,
  faCheckDouble,
  faChevronRight,
  faMapLocationDot,
  faIndianRupeeSign,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
  Snippet,
} from "@nextui-org/react"
import copy from "clipboard-copy"

import { setCurrentParty } from "../../../state/outstandingReceivableSlice"
import {
  useFetchWithBody,
  usePost,
} from "../../../api/reactQueryCustomHooks.jsx"
import { apiRoute, linksUrl, outTypeEnum } from "../../../constants/enum.js"
import { calculateTotal } from "../../../Utility/utility.js"
import {
  ErrorCard,
  LoadingCard,
  MainContainer,
  NextUITable,
} from "../../../components/index.js"
import {
  COLUMN_ALIGN_TYPE,
  FOOTER_TYPE,
  ROW_ACTION_BTN_TYPE,
} from "../../../components/NextUITable.jsx"

const OutRPartySummary = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [selectedKey, setSelectedKey] = useState(new Set());

  const { compCode } = useSelector((state) => state.company)

  const {
    isOpen: isOpenAddressModel,
    onOpen: onOpenAddressModel,
    onOpenChange: onOpenAddressModelChange,
  } = useDisclosure()

  const {
    isLoading,
    isError,
    data = [],
  } = useFetchWithBody({
    url: apiRoute.outstanding.partySummary,
    queryKey: "OutstandingReceivablePartySummary",
    inputData: {
      compcode: compCode.join(","),
      out_type: outTypeEnum.R,
    },
  })

  const {
    isPending: isAddressPending,
    data: addressData = [],
    isError: isErrorFetch,
    mutate: fetchAddress,
  } = usePost({
    url: apiRoute.outstanding.partyInfo,
    queryKey: "OutstandingReceivablePartySummary-Address",
  })

  const totalBalanceAmount = useMemo(
    () =>
      calculateTotal({
        data: data,
        columnName: "Balance_Amount",
      }),
    [data]
  )

  let downloadDataFooter = {
    party: "",
    Balance_Amount: "Total: " + totalBalanceAmount,
  }

  const exportColumns = [
    { uid: "party", name: "Party" },
    { uid: "Balance_Amount", name: "Balance Amount" },
  ]

  const INITIAL_VISIBLE_COLUMNS = ["party", "actions"]

  const columns = [
    {
      name: "Party Name",
      uid: "party",
      sortable: true,
      secondCol: {
        name: "Balance_Amount",
        icon: <FontAwesomeIcon icon={faIndianRupeeSign} size="sm" />,
      },
      footer: {
        type: FOOTER_TYPE.text,
        label: "Total: ",
        value: totalBalanceAmount,
      },
    },
    {
      name: "Balance Amount",
      uid: "Balance_Amount",
    },
    {
      name: "ACTIONS",
      uid: "actions",
      //align:COLUMN_ALIGN_TYPE.center,
    },
  ]

  const rowActionBtn = [
    {
      icon: <FontAwesomeIcon icon={faLocationDot} />,
      onClick: (row) => {
        fetchAddress({
          data: {
            compcode: compCode.join(","),
            party_id: row.party_id,
          },
        })
        onOpenAddressModel()
      },
      label: "Address",
      type: ROW_ACTION_BTN_TYPE.link,
    },
    {
      icon: <FontAwesomeIcon icon={faChevronRight} />,
      onClick: (row) => {
        dispatch(setCurrentParty(row))
        navigate(linksUrl.outstanding.receivable.partyAgingSummary)
      },
      label: "next",
      type: ROW_ACTION_BTN_TYPE.link,
    },
  ]

  const addressModel = () => {
    return (
      <Modal
        isOpen={isOpenAddressModel}
        onOpenChange={onOpenAddressModelChange}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Address</ModalHeader>
              <ModalBody className="overflow-auto">
                {isAddressPending ? (
                  "Loading..."
                ) : (
                  <div>
                    {addressData && addressData[0]?.Party_Address && (
                      <div className="flex flex-col gap-2">
                        <div className="flex  items-center gap-1 overflow-auto">
                          <FontAwesomeIcon icon={faMapLocationDot} />
                          {/* address */}
                          <Snippet
                            hideSymbol
                            className="w-full"
                            checkIcon={<FontAwesomeIcon icon={faCheckDouble} />}
                            onCopy={(value) => {
                              copy(value)
                            }}
                          >
                            {addressData[0]?.Party_Address?.split(",").map(
                              (item, index) => {
                                return <span key={index}>{item}</span>
                              }
                            )}
                          </Snippet>
                        </div>
                        <div className="flex  items-center gap-1 overflow-auto">
                          <FontAwesomeIcon icon={faPhone} />
                          {/* contact number */}
                          <Snippet
                            hideSymbol
                            className="w-full"
                            checkIcon={<FontAwesomeIcon icon={faCheckDouble} />}
                            onCopy={(value) => {
                              copy(value)
                            }}
                          >
                            {addressData[0]?.Party_ContectNo?.split(",").map(
                              (item, index) => {
                                return <span key={index}>{item}</span>
                              }
                            )}
                          </Snippet>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    )
  }
  
  const handleSelectionChange = (payload) => {


    const { e } = payload;

    // 🔥 CASE 1: Select all
    if (e === "all") {
      const allKeys = data.map((row) => String(row.party_id));
      setSelectedKey(new Set(allKeys));
      return;
    }

    // 🔥 CASE 2: empty selection
    if (!e) {
      setSelectedKey(new Set());
      return;
    }

    // 🔥 CASE 3: if Set
    if (e instanceof Set) {
      setSelectedKey(e);
      return;
    }

    // 🔥 CASE 4: array
    if (Array.isArray(e)) {
      setSelectedKey(new Set(e));
      return;
    }

    // 🔥 CASE 5: fallback (single value or object)
    if (typeof e === "string" || typeof e === "number") {
      setSelectedKey(new Set([e]));
      return;
    }

    console.warn("Unknown selection format:", e);

  };

  if (isLoading) {
    return <LoadingCard />
  }

  if (isError || isErrorFetch) {
    return <ErrorCard />
  }

  return (
    <div>
      <MainContainer
        pageName="Party Summary"
        info={[]}
        breadcrumbs={["Receivable", "Party Summary"]}
      >
        {addressModel({ data: addressData })}
        <NextUITable
          INITIAL_VISIBLE_COLUMNS={INITIAL_VISIBLE_COLUMNS}
          data={data}
          columns={columns}
          exportColumns={exportColumns}
          rowActionBtn={rowActionBtn}
          downloadDataFooter={downloadDataFooter}
          showAdvanceFilter={false}
          downloadFileName="Receivable_Outstanding_Summary"
          handleSelectionChange={handleSelectionChange}
          selectedKeys={selectedKey}
          setSelectedKeys={setSelectedKey}
          id="party_id"
        />
      </MainContainer>
    </div>
  )
}

export default OutRPartySummary
